import { scheduleDateAtom } from '@/atoms/sessionAtoms';
import { openedSessionTabAtom, openedSessionTabEnum } from '@/atoms/sideNavAtoms';
import { canCollapseAtom } from '@/components/layout/header/mobile-app-header/mobile-app-header.atoms';
import { isMobileHeaderSheetOpenAtom } from '@/components/layout/header/mobile-app-header/mobile-header-sheet-atoms';
import { useUpcomingSessionsList } from '@/features/upcomingSessionTab/hooks/useUpcomingSessionsList';
import { useIntegrationEhrInfo } from '@/hooks/integrations/useIntegrationEhrInfo';
import { useIsMobile } from '@/hooks/useWindowDimensions';
import { cn } from '@/lib/cn';
import {
  trackScheduleSynced,
  trackScheduleViewed,
} from '@/models/analytics/features/trackSessionScheduleAnalytics';
import { Button } from '@heidi/design-system/button';
import { Calendar } from '@heidi/design-system/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@heidi/design-system/popover';
import { isSameDay } from 'date-fns';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { Calendar as LucideCalendar, PanelLeftClose, RefreshCw, X } from 'lucide-react';
import { memo, useCallback } from 'react';

function UpcomingTabHeaderButtonsV2() {
  const isMobile = useIsMobile();
  const openedSessionTab = useAtomValue(openedSessionTabAtom);
  const { integrationEhrProvider } = useIntegrationEhrInfo();

  const [scheduleDate, setScheduleDate] = useAtom(scheduleDateAtom);
  const { refetchUpcomingSessions, isPending: isPendingUpcomingSessions } =
    useUpcomingSessionsList();
  const handleSync = useCallback(() => {
    void refetchUpcomingSessions();
    trackScheduleSynced({
      ehr_provider: integrationEhrProvider,
    });
  }, [integrationEhrProvider, refetchUpcomingSessions]);
  const isToday = isSameDay(scheduleDate ?? new Date(), new Date());

  const canCollapse = useAtomValue(canCollapseAtom);
  const setIsMobileHeaderSheetOpen = useSetAtom(isMobileHeaderSheetOpenAtom);
  if (openedSessionTab !== openedSessionTabEnum.UPCOMING) {
    return <></>;
  }

  return (
    <>
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-0.5">
          <Button
            disableAutomaticSvgSizing
            variant="ghost"
            size="xxs"
            iconOnly
            className={cn('text-text-secondary', isMobile && 'p-1')}
            onClick={handleSync}
          >
            <RefreshCw
              size={isMobile ? 24 : 12}
              className={cn(isPendingUpcomingSessions && 'animate-spin')}
            />
          </Button>

          <Popover>
            <PopoverTrigger asChild>
              <Button
                disableAutomaticSvgSizing
                size="xxs"
                variant={!isToday ? 'default' : isMobile ? 'ghost' : 'secondary'}
                className={cn(
                  'p-1 text-text-secondary',
                  !isToday && 'text-text-primary-invert',
                  isMobile && 'p-1'
                )}
              >
                <LucideCalendar size={isMobile ? 24 : 12} />
                {!isToday && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setScheduleDate(undefined);
                    }}
                  >
                    <X size={12} />
                  </button>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-fit">
              <Calendar
                mode="single"
                selected={scheduleDate}
                onSelect={(date) => {
                  setScheduleDate(date);
                  trackScheduleViewed({
                    date,
                    ehr_provider: integrationEhrProvider,
                  });
                }}
              />
            </PopoverContent>
          </Popover>

          {isMobile && canCollapse !== false && (
            <div className="flex items-center gap-2">
              <Button
                disableAutomaticSvgSizing
                variant="ghost"
                iconOnly
                size="xxs"
                className="p-1 text-text-secondary"
                disabled={isPendingUpcomingSessions}
                onClick={() => {
                  setIsMobileHeaderSheetOpen(false);
                }}
              >
                <PanelLeftClose size={24} />
              </Button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default memo(UpcomingTabHeaderButtonsV2);
